import { apiService } from "../services/api";
import type { AppDispatch } from "../state/store";
import { showConfirmDialog } from "../state/utils/confirmationDialogUtils";

export const LEDGER_NAME_SIMILARITY_CONFIRM_TITLE = "";

export type SimilarLedgerMatch = {
  id: string;
  ledger_name: string;
};

export type SimilarLedgerNameCheckResult = {
  has_similar: boolean;
  matches: SimilarLedgerMatch[];
  message?: string;
};

const CUSTOMER_SCREEN_IDS = new Set(["123", "123_add_customer"]);
const VENDOR_SCREEN_IDS = new Set(["128", "128_add_vendor"]);
const HEADS_SCREEN_IDS = new Set(["135", "135_heads_add"]);

const CUSTOMER_MODULES = new Set([
  "Customer_Add",
  "customer_add",
  "customer_add_edit",
]);
const VENDOR_MODULES = new Set([
  "Supplier_Add",
  "supplier_add",
  "supplier_add_edit",
  "vendor_add",
  "vendor_add_edit",
]);
const HEADS_MODULES = new Set([
  "heads_add",
  "heads_add_from_modal",
  "heads_add_assets",
  "heads_add_from_bank",
]);

export const isLedgerNameSimilarityContext = (
  screenId?: string | null,
  moduleEndpoint?: string | null
): boolean => {
  const sid = String(screenId || "").trim();
  const module = String(moduleEndpoint || "").trim();
  if (
    CUSTOMER_SCREEN_IDS.has(sid) ||
    VENDOR_SCREEN_IDS.has(sid) ||
    HEADS_SCREEN_IDS.has(sid)
  ) {
    return true;
  }
  if (
    CUSTOMER_MODULES.has(module) ||
    VENDOR_MODULES.has(module) ||
    HEADS_MODULES.has(module)
  ) {
    return true;
  }
  return (
    sid.includes("customer_add") ||
    sid.includes("vendor_add") ||
    sid.includes("supplier_add") ||
    sid.includes("heads_add") ||
    module.toLowerCase().includes("customer_add") ||
    module.toLowerCase().includes("supplier_add") ||
    module.toLowerCase().includes("vendor_add") ||
    module.toLowerCase().includes("heads_add")
  );
};

export const resolveLedgerNameForSimilarityCheck = (
  data: Record<string, any> | null | undefined,
  screenId?: string | null,
  moduleEndpoint?: string | null
): string => {
  if (!data || typeof data !== "object") return "";
  const sid = String(screenId || "").trim();
  const module = String(moduleEndpoint || "").trim();

  const pick = (...keys: string[]): string => {
    for (const key of keys) {
      const val = String(data[key] ?? "").trim();
      if (val) return val;
    }
    return "";
  };

  if (
    CUSTOMER_SCREEN_IDS.has(sid) ||
    CUSTOMER_MODULES.has(module) ||
    sid.includes("customer") ||
    module.toLowerCase().includes("customer")
  ) {
    return pick("customername", "txt_customer_name", "ledger_name");
  }

  if (
    VENDOR_SCREEN_IDS.has(sid) ||
    VENDOR_MODULES.has(module) ||
    sid.includes("vendor") ||
    sid.includes("supplier") ||
    module.toLowerCase().includes("supplier") ||
    module.toLowerCase().includes("vendor")
  ) {
    return pick("suppliername", "txt_suppliername", "ledger_name");
  }

  return pick(
    "ledger_name",
    "txt_name",
    "name",
    "customername",
    "txt_customer_name",
    "suppliername",
    "txt_suppliername"
  );
};

export async function fetchSimilarLedgerNameCheck(params: {
  name: string;
  excludeId?: string;
}): Promise<SimilarLedgerNameCheckResult> {
  const name = String(params.name || "").trim();
  if (!name) {
    return { has_similar: false, matches: [] };
  }

  const response = await apiService.post<
    SimilarLedgerNameCheckResult & { success?: boolean }
  >(
    "/pabws/module/ledger_name/check_similar",
    {
      name,
      exclude_id: params.excludeId?.trim() || undefined,
    },
    {
      includeAuthToken: true,
      authHeaderType: "Bearer",
      errorHandling: "throw",
    }
  );

  return {
    has_similar: Boolean(response?.has_similar),
    matches: Array.isArray(response?.matches) ? response.matches : [],
    message: response?.message,
  };
};

export const formatSimilarLedgerConfirmMessage = (
  matches: SimilarLedgerMatch[],
  fallbackMessage?: string
): string => {
  if (fallbackMessage && fallbackMessage.trim()) {
    return fallbackMessage.trim();
  }
  const bullets = matches
    .map((m) => `• ${m.ledger_name || "Ledger"}`)
    .join("\n");
  return (
    "A similar name already exists:\n\n" +
    `${bullets}\n\n` +
    "Do you want to continue anyway?"
  );
};

export async function confirmSimilarLedgerNameProceed(
  dispatch: AppDispatch,
  matches: SimilarLedgerMatch[],
  message?: string
): Promise<boolean> {
  if (!matches.length) return true;
  return showConfirmDialog(
    dispatch,
    formatSimilarLedgerConfirmMessage(matches, message),
    LEDGER_NAME_SIMILARITY_CONFIRM_TITLE
  );
}

export async function validateSimilarLedgerNameForSave(params: {
  name: string;
  excludeId?: string;
}): Promise<SimilarLedgerNameCheckResult> {
  return fetchSimilarLedgerNameCheck(params);
}
